import getDomain from '../utils/url/getDomain';
import { idbDeleteKeyPair, idbGetKeyPair, idbProbe, idbPutKeyPair, LEGACY_RECORD_KEY } from './db';
import { isDpopAuthEnabled } from './dpopAuthEnabled';
import type { DpopKeyPair, DpopPublicKey } from './key';
import { isDpopPublicKey } from './key';

// fallback for a database that starts dropping writes after the boot-time probe
const memoryKeyPairs = new Map<string, DpopKeyPair>();

// keys are stored per API ORIGIN: a page can host an embed authenticating to another backend, and
// one slot for both means the second login orphans the first session's key.

// no origin resolvable (SSR, native without location) — carries a scheme, so it cannot collide
// with db.ts's reserved record keys
const UNKNOWN_ORIGIN = 'unknown:';

function toKeyOrigin(origin?: string): string {
  try {
    // getDomain is the precedence getBaseURL uses to pick where the request goes, resolved against
    // the page the way the browser resolves it — on the BE-rewrite hosts __unifyBaseUrl is a
    // RELATIVE prefix ('/support'), which the signing hook would never match against url.origin.
    // Normalizing also stops a path, a trailing slash or a :443 minting a second key.
    const base = typeof window === 'undefined' ? undefined : window.location?.href;
    return new URL(origin ?? getDomain() ?? '', base).origin;
  } catch {
    return UNKNOWN_ORIGIN;
  }
}

// one legacy record predates per-origin keys; whichever origin misses first copies it under itself.
// Copied, NOT moved: mid-rollout a tab on the old bundle reads only that record, and the origin
// that misses first need not own the key. Retired on logout instead, where the key is dead anyway.
async function readLegacyKeyPair(recordKey: string): Promise<DpopKeyPair | null> {
  const stored = await idbGetKeyPair(LEGACY_RECORD_KEY);
  if (!isStoredDpopKeyPair(stored)) return null;
  try {
    // a login can land here while the legacy record is being read; overwriting it would sign
    // every request with a key the session is not bound to, and nothing repairs that
    const current = await idbGetKeyPair(recordKey);
    if (isStoredDpopKeyPair(current)) return current;
    await idbPutKeyPair(recordKey, stored);
  } catch (error) {
    // a database that cannot persist must not cost us the key we just read successfully
    console.warn('Failed to re-file the auth key:', error);
  }
  return stored;
}

function isStoredDpopKeyPair(value: unknown): value is DpopKeyPair {
  if (!value || typeof value !== 'object') return false;
  const pair = value as Record<string, unknown>;
  return (
    isDpopPublicKey(pair.publicKey) &&
    typeof CryptoKey !== 'undefined' &&
    pair.privateKey instanceof CryptoKey
  );
}

async function readStoredKeyPair(recordKey: string): Promise<DpopKeyPair | null> {
  try {
    const stored = await idbGetKeyPair(recordKey);
    // a broken record must not replace what the session is actually using
    if (isStoredDpopKeyPair(stored)) return stored;
    if (stored !== undefined) console.warn('Ignoring a broken IndexedDB record');
    // memory first: it holds what this session is using when writes are being dropped
    return memoryKeyPairs.get(recordKey) ?? (await readLegacyKeyPair(recordKey)) ?? null;
  } catch (error) {
    console.warn('Failed to read from IndexedDB:', error);
    return memoryKeyPairs.get(recordKey) ?? null;
  }
}

// a burst of requests shares one read. Per origin, or a host page's read is served the embed's key
const inflightReads = new Map<string, Promise<DpopKeyPair | null>>();

// never cached, so another tab logging in updates this tab's key too. Never rejects.
export function readDpopKeyPair(origin?: string): Promise<DpopKeyPair | null> {
  const recordKey = toKeyOrigin(origin);
  const inflight = inflightReads.get(recordKey);
  if (inflight) return inflight;

  const read = readStoredKeyPair(recordKey).finally(() => {
    inflightReads.delete(recordKey);
  });
  inflightReads.set(recordKey, read);
  return read;
}

export async function getDpopPublicKey(origin?: string): Promise<DpopPublicKey | null> {
  return (await readDpopKeyPair(origin))?.publicKey ?? null;
}

// resolves once the pair is saved, or kept in memory if that failed. Never rejects.
export async function saveDpopKeyPair(keyPair: DpopKeyPair, origin?: string): Promise<void> {
  const recordKey = toKeyOrigin(origin);
  memoryKeyPairs.set(recordKey, keyPair);
  try {
    await idbPutKeyPair(recordKey, keyPair);
  } catch (error) {
    // best effort — memory still works, but only until this tab reloads
    console.warn('Failed to write to IndexedDB:', error);
  }
}

// clears one backend's key only: logging out of the host app must not unsign a live embed
export async function clearDpopKeyPair(origin?: string): Promise<void> {
  const recordKey = toKeyOrigin(origin);
  memoryKeyPairs.delete(recordKey);
  try {
    await idbDeleteKeyPair(recordKey);
    // leaving the pre-origin record would let the next read resurrect it under a new session
    await idbDeleteKeyPair(LEGACY_RECORD_KEY);
  } catch (error) {
    // memory is already cleared, but a stale record is left behind
    console.warn('Failed to clear IndexedDB:', error);
  }
}

export async function isDpopSupported(): Promise<boolean> {
  // an opted-out release reads as "unsupported", so one check turns off every path
  if (!isDpopAuthEnabled()) {
    return false;
  }
  // checked now, not at signing time, or we bind a key we can never sign with
  if (!globalThis.crypto?.subtle || typeof crypto.randomUUID !== 'function') {
    return false;
  }
  return await idbProbe();
}
